import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { setLoading } from 'Actions/loading';
import { setFlow } from 'Actions/flow';
import { Box, Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle, Checkbox, FormControlLabel } from '@mui/material';
import { useSnackbar } from 'notistack';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import http from 'Utils/http';
import { useCreateUrl } from 'Hooks/useCreateUrl';
import { Transition } from 'Shared/Transition';
import { process } from 'Utils/errorHandler';

// Identifier that stays with the flow across environments. Generated here so
// that regenerating it does not need a round trip; the server rejects a value
// another flow already owns.
const generateExternalId = () => {
    const bytes = new Uint8Array(6);
    window.crypto.getRandomValues(bytes);
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

    return `FLOW-${hex.toUpperCase()}`;
};

const FlowEditForm = ({ flowId, name, description, external_id, lbh_status, show_in_root, visibleEditFlowForm, setVisibleEditFlowForm }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { createUrl } = useCreateUrl();

    const {
        register,
        control,
        handleSubmit,
        reset,
        setError,
        formState: {errors, isDirty},
    } = useForm({
        defaultValues: {
            name: name || '',
            description: description || '',
            external_id: external_id || '',
            lbh_status: lbh_status ?? false,
            show_in_root: show_in_root ?? false,
        },
    });

    const onClose = () => {
        reset();
        setVisibleEditFlowForm(false);
    };

    useEffect(() => {
        reset({
            name: name || '',
            description: description || '',
            external_id: external_id || '',
            lbh_status: lbh_status ?? false,
            show_in_root: show_in_root ?? false,
        });
    }, [name, description, external_id, lbh_status, show_in_root, reset]);

    const onSubmit = (data) => {
        const params = {
            name: data.name,
            description: data.description.replace(/\s*\n+\s*/g, ' ').trim(),
            external_id: data.external_id,
            lbh_status: data.lbh_status,
            show_in_root: data.show_in_root,
        };
        dispatch(setLoading(true));
        http.post(createUrl(`/flow/${flowId}/update`), params).then((res) => {
            dispatch(setFlow(res.data.flow));
            setVisibleEditFlowForm(false);
            enqueueSnackbar('Flow has been updated', {
                action: CustomSnackbar,
                variant: 'success'
            });
        }).catch((err) => {
            const externalIdError = err.response?.status === 422 && err.response?.data?.errors?.external_id?.[0];
            if (externalIdError) {
                setError('external_id', { message: externalIdError });
                return;
            }
            process(err, enqueueSnackbar);
        }).finally(() => {
            dispatch(setLoading(false));
        });
    };

    const checkboxField = (fieldName, label) => (
        <FormControlLabel
            control={
                <Controller
                    name={fieldName}
                    control={control}
                    render={({ field }) => (
                        <Checkbox
                            {...field}
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                        />
                    )}
                />
            }
            label={label}
            sx={{
                marginTop: '10px',
                '& .MuiTypography-root': {
                    color: '#3B465C',
                },
            }}
        />
    );

    return (
        <Dialog
            open={visibleEditFlowForm}
            onClose={onClose}
            TransitionComponent={Transition}
            sx={{
                '& .MuiDialog-container': {
                    alignItems: 'flex-start',
                    paddingTop: '150px',
                },
                '& .MuiDialog-paper': {
                    width: '50%',
                    minWidth: '320px',
                    maxWidth: 'none',
                    margin: 0,
                },
            }}
        >
            <DialogTitle
                className="h1"
                variant="h1"
                sx={{
                    paddingBottom: '6px',
                    textAlign: 'center',
                    color: '#3B465C',
                }}
            >
                Edit Flow
            </DialogTitle>
            <DialogContent>
                <Box
                    component="form"
                    onSubmit={handleSubmit(onSubmit)}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <TextField
                        error={errors?.name ? true : false}
                        helperText={errors?.name ? 'Flow name is required, at most 255 characters' : ''}
                        fullWidth
                        id="name"
                        label="Flow name"
                        name="name"
                        inputProps={{ maxLength: 255 }}
                        sx={{
                            marginTop: '10px',
                        }}
                        {...register('name', {
                            required: true,
                            maxLength: 255,
                        })}
                    />
                    <TextField
                        error={errors?.description ? true : false}
                        helperText={errors?.description ? 'Description must be at most 255 characters' : ''}
                        fullWidth
                        id="description"
                        label="Description"
                        name="description"
                        multiline
                        inputProps={{ maxLength: 255 }}
                        sx={{
                            marginTop: '20px',
                        }}
                        {...register('description', {
                            maxLength: 255,
                        })}
                    />
                    <TextField
                        error={errors?.external_id ? true : false}
                        helperText={errors?.external_id?.message || ''}
                        fullWidth
                        id="external_id"
                        label="External ID"
                        name="external_id"
                        inputProps={{ maxLength: 64, readOnly: true }}
                        sx={{
                            marginTop: '20px',
                        }}
                        {...register('external_id', {
                            required: true,
                            maxLength: 64,
                        })}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: '30px' }}>
                        {checkboxField('lbh_status', 'Loan Boarding Hub Flow')}
                        {checkboxField('show_in_root', 'Always show in root')}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions
                sx={{
                    padding: '16px 24px',
                }}
            >
                <Button onClick={onClose} variant="outlined">
                    Close
                </Button>
                <Button onClick={handleSubmit(onSubmit)} variant="contained" disabled={!isDirty}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FlowEditForm;
